import { useState, useEffect } from 'react';
import AppNav from './appNav';
import IconButton from './iconButton';
import IconButtonLink from './iconButtonLink';
import { Link } from 'react-router';
import Tooltip from '@mui/material/Tooltip';
import { useClaims } from '../auth/useClaims';
import { api } from '../api/apiClient';
import { useMsal } from '@azure/msal-react';
import { useSelectedAccount } from '../App';
import { useLocation } from 'react-router-dom';

import CurrencyPoundIcon from '@mui/icons-material/CurrencyPoundOutlined';
import BuildIcon from '@mui/icons-material/BuildOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import ReportProblemIcon from '@mui/icons-material/ReportProblemOutlined';
import MailIcon from '@mui/icons-material/MailOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import MenuIcon from '@mui/icons-material/MenuOutlined';
import CloseIcon from '@mui/icons-material/CloseOutlined';

interface Account {
    accountReference: string;
    description: string;
    balance: number;
    subAccounts: any[];
    agreementDescription?: string;
    isFormerAccount: boolean;
    assetId: number;
    tenancyStartDate: string;
    tenancyEndDate: string;
    rentChargeEffectives: any;
}

interface AppHeaderProps {
    displayInfo?: boolean;
}

const AppHeader = ({ displayInfo = true }: AppHeaderProps) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const { selectedAccount, setSelectedAccount } = useSelectedAccount();
    const { name } = useClaims();
    const { instance, accounts: msalAccounts } = useMsal();
    const location = useLocation();
    const activeAccount = instance.getActiveAccount() || msalAccounts[0];
    const accountId = activeAccount?.homeAccountId;

    const toggleNav = () => setIsOpen(prev => !prev);

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Morning';
        if (hour < 18) return 'Afternoon';
        return 'Evening';
    };

    useEffect(() => {
        if (!accountId) {
            return;
        }


        const fetchAccounts = async () => {
            try {
                const response: Account[] = await api<Account[]>('/account/accounts');
                setAccounts(response);
                if (!selectedAccount && response.length > 0) {
                    setSelectedAccount(response[0]);
                }
            } catch (err) {
                console.error('Failed to load accounts', err);
            }
        };

        fetchAccounts();
    }, [accountId]);

    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    const handleAccountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const account = accounts.find(acc => acc.accountReference === e.target.value);
        if (account) {
            setSelectedAccount(account);
        }
    };

    const navigationItems = [
        { label: 'Make a payment', path: '/myaccount', icon: <CurrencyPoundIcon /> },
        { label: 'Report a repair', path: 'https://www.accentgroup.org/your-home/repairs-and-maintenance/', icon: <BuildIcon /> },
        { label: 'View documents', path: '/mydocuments', icon: <DescriptionIcon /> },
        { label: 'Report anti-social behaviour', path: 'https://www.accentgroup.org/your-home/anti-social-behaviour/report-difficult-behaviour-in-your-area/', icon: <ReportProblemIcon /> },
        { label: 'Contact us', path: 'https://www.accentgroup.org/contact-us/', icon: <MailIcon /> },
        { label: 'View profile', path: '/myprofile', icon: <PersonIcon /> },
    ];

    const renderGreeting = () => (
        <div className="app-header__greeting-content">
            <h2 className="app-header__greeting-header">
                {name ? `${getGreeting()}, ${name.split(' ')[0]}` : ''}
            </h2>
            <div className="app-header__greeting-account">
                <select id="account-select" value={selectedAccount?.accountReference || ''} onChange={handleAccountChange} className="account-dropdown">
                    {accounts.map(acc => (
                        <option key={acc.accountReference} value={acc.accountReference}>
                            {acc.description}{acc.isFormerAccount ? ' (Former)' : ''}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );

    const renderNavigationIcons = () => (
        <div className="app-header-indicators__wrap">
            {navigationItems.map((item) => {
                const isExternal = item.path.startsWith('http');

                return (
                    <Tooltip key={item.label} title={item.label} placement="bottom" arrow>
                        <div className="tooltip">
                            {isExternal ? (
                                <IconButton cssClasses="btn--icon-only__basic" icon={item.icon} ariaLabel={item.label} onClick={() => window.open(item.path, '_blank', 'noopener,noreferrer')} />                            
                            ) : (
                                <IconButtonLink cssClasses="btn--icon-only__basic" icon={item.icon} to={item.path} ariaLabel={item.label} />                            
                            )}
                        </div>
                    </Tooltip>
                );
            })}
        </div>
    );

    const renderMenuButton = (extraClasses = '', showMenuText = true) => (
        <IconButton cssClasses={`btn--icon-only__basic ${extraClasses}`} icon={<MenuIcon />} ariaLabel="Menu" onClick={toggleNav}>
            {showMenuText ? 'Menu' : ''}
        </IconButton>
    );

    return (
        <>
            <header className="app-header">
                <Link className="app-header__logo" to="/">
                    <img className="logo-img" src="/images/logos/accent-logo-white--no-bg.png" alt="Accent Logo" />
                    <h1 className="title--h1 logo-name">MyAccount</h1>
                </Link>

                {displayInfo && (
                    <div className="app-header__indicators">
                        {renderNavigationIcons()}
                        {renderMenuButton('app-header__menu-button', false)}
                    </div>
                )}

                {displayInfo && (
                    <div className={`app-nav-backdrop ${isOpen ? 'open' : ''}`} onClick={toggleNav}>
                        <div className={`app-nav-slideout ${isOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
                            <button onClick={toggleNav} className="btn btn--close close-btn" aria-label="Close modal">
                                <CloseIcon aria-hidden="true" />
                            </button>
                            <AppNav isOpen={isOpen} />
                        </div>
                    </div>
                )}
            </header>

            {displayInfo && name && <div className="app-header__greeting">{renderGreeting()}</div>}

            {displayInfo && (
                <div className="action-bar">
                    {name && <div className="left-controls">{renderGreeting()}</div>}
                    <div className="right-controls">{renderMenuButton('menu-button')}</div>
                </div>
            )}
        </>
    );
};

export default AppHeader;
